import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BookOpen, Camera, ChevronLeft, ChevronRight, Search, Star, Table2 } from 'lucide-react';
import './styles.css';

const BOOK_CONFIG = {
  visibleNeighbors: 8,
  swipeThreshold: 44,
  itemClass: 'book-item',
  perspective: 1100,
  gap: 96,
  stack: 34,
  rotation: 56,
  depth: -300
};

const PHOTO_CONFIG = {
  visibleNeighbors: 4,
  swipeThreshold: 44,
  itemClass: 'photo-item',
  perspective: 1200,
  gap: 82,
  stack: 24,
  rotation: 50,
  depth: -280
};

function ErrorBoundaryFallback({ error }) {
  return (
    <div className="app-shell app-center">
      <div className="error-panel">
        <BookOpen size={32} />
        <h1>Library failed to load.</h1>
        <p>{error?.message || 'Refresh the page, or try again in a minute.'}</p>
      </div>
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) return <ErrorBoundaryFallback error={this.state.error} />;
    return this.props.children;
  }
}

function StarRating({ rating }) {
  const value = Number(rating) || 0;
  return (
    <div className="stars" aria-label={`${value} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, index) => (
        <Star
          key={index}
          size={14}
          fill={index < value ? 'currentColor' : 'none'}
          className={index < value ? 'star-on' : 'star-off'}
        />
      ))}
    </div>
  );
}

function StandInCover({ title, author }) {
  return (
    <div className="stand-in-cover">
      <div className="spine" />
      <div className="stand-in-inner">
        <h3>{title}</h3>
        <div />
        <p>{author}</p>
      </div>
    </div>
  );
}

function ImageWithFallback({ item, mode, reflected = false }) {
  const [failed, setFailed] = useState(false);
  const src = mode === 'library' ? item.coverUrl : item.image?.large || item.url;

  if (!src || failed) {
    return mode === 'library' ? (
      <StandInCover title={item.title} author={item.author} />
    ) : (
      <div className="photo-fallback"><Camera size={42} /></div>
    );
  }

  return (
    <img
      src={src}
      srcSet={mode === 'gallery' && item.image ? `${item.image.small} 720w, ${item.image.large} 1440w` : undefined}
      sizes={mode === 'gallery' ? '(max-width: 700px) 86vw, 62vw' : undefined}
      alt={reflected ? '' : item.title}
      className={mode === 'library' ? 'cover-image' : 'photo-image'}
      loading={reflected ? 'lazy' : item.priority ? 'eager' : 'lazy'}
      decoding="async"
      draggable="false"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

function CoverFlow({ items, mode, activeIndex, setActiveIndex }) {
  const config = mode === 'library' ? BOOK_CONFIG : PHOTO_CONFIG;
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const drag = useRef({ startX: 0, startY: 0, offset: 0 });
  const scrollTimer = useRef(null);

  const moveBy = useCallback((direction) => {
    setActiveIndex((current) => Math.max(0, Math.min(items.length - 1, current + direction)));
  }, [items.length, setActiveIndex]);

  const stopScrolling = useCallback(() => {
    if (scrollTimer.current) window.clearInterval(scrollTimer.current);
    scrollTimer.current = null;
  }, []);

  const startScrolling = useCallback((direction) => {
    stopScrolling();
    moveBy(direction);
    scrollTimer.current = window.setInterval(() => moveBy(direction), 180);
  }, [moveBy, stopScrolling]);

  useEffect(() => stopScrolling, [stopScrolling]);

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'ArrowLeft') moveBy(-1);
      if (event.key === 'ArrowRight') moveBy(1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [moveBy]);

  const onPointerDown = (event) => {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drag.current = { startX: event.clientX, startY: event.clientY, offset: 0 };
    setIsDragging(true);
  };

  const onPointerMove = (event) => {
    if (!isDragging) return;
    const deltaX = event.clientX - drag.current.startX;
    const deltaY = event.clientY - drag.current.startY;
    if (Math.abs(deltaX) > Math.abs(deltaY)) event.preventDefault();
    drag.current.offset = deltaX;
    window.requestAnimationFrame(() => setDragOffset(deltaX));
  };

  const onPointerEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (Math.abs(drag.current.offset) > config.swipeThreshold) {
      moveBy(drag.current.offset > 0 ? -1 : 1);
    }
    drag.current.offset = 0;
    setDragOffset(0);
  };

  const width = typeof window === 'undefined' ? 1000 : window.innerWidth;
  const dragShift = -dragOffset / (width * (mode === 'library' ? 0.42 : 0.68));

  return (
    <main className="flow-wrap" style={{ '--perspective': `${config.perspective}px` }}>
      <button
        className="nav-button nav-left"
        type="button"
        aria-label="Previous item"
        onPointerDown={() => startScrolling(-1)}
        onPointerUp={stopScrolling}
        onPointerLeave={stopScrolling}
        onClick={() => moveBy(-1)}
      >
        <ChevronLeft size={30} />
      </button>

      <section
        className={`flow-stage ${mode}`}
        aria-label={mode === 'library' ? 'Book cover flow' : 'Photo cover flow'}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onPointerLeave={onPointerEnd}
      >
        {items.map((rawItem, index) => {
          if (Math.abs(activeIndex - index) > config.visibleNeighbors + 1) return null;

          const offset = index - activeIndex - dragShift;
          const absOffset = Math.abs(offset);
          const sign = Math.sign(offset);
          const item = {
            ...rawItem,
            priority: absOffset <= 1
          };

          const centered = absOffset < 0.08;
          const x = centered ? offset * (mode === 'library' ? 118 : 150) : sign * (config.gap + Math.max(0, absOffset - 1) * config.stack);
          const z = centered ? 56 : config.depth - absOffset * 44;
          const rotate = centered ? offset * -9 : sign * -config.rotation;

          return (
            <article
              key={item.id}
              className={`flow-item ${config.itemClass}`}
              style={{
                '--x': centered ? `${x}px` : `${x}%`,
                '--z': `${z}px`,
                '--rotate': `${rotate}deg`,
                '--brightness': Math.max(0.34, 1 - absOffset * 0.18),
                '--opacity': absOffset > config.visibleNeighbors ? 0 : 1,
                zIndex: 100 - Math.round(absOffset)
              }}
              onClick={() => setActiveIndex(index)}
              aria-label={item.title}
            >
              <div className="item-surface">
                <ImageWithFallback item={item} mode={mode} />
                <div className="reflection" aria-hidden="true">
                  <ImageWithFallback item={item} mode={mode} reflected />
                </div>
              </div>
            </article>
          );
        })}
      </section>

      <button
        className="nav-button nav-right"
        type="button"
        aria-label="Next item"
        onPointerDown={() => startScrolling(1)}
        onPointerUp={stopScrolling}
        onPointerLeave={stopScrolling}
        onClick={() => moveBy(1)}
      >
        <ChevronRight size={30} />
      </button>
    </main>
  );
}

function BookTable({ books }) {
  const [sort, setSort] = useState({ key: 'title', direction: 'asc' });

  const sorted = useMemo(() => {
    return [...books].sort((a, b) => {
      const direction = sort.direction === 'asc' ? 1 : -1;
      if (sort.key === 'rating') return ((a.rating || 0) - (b.rating || 0)) * direction;
      return String(a[sort.key] || '').localeCompare(String(b[sort.key] || '')) * direction;
    });
  }, [books, sort]);

  const setColumn = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const headers = [
    ['title', 'Book'],
    ['author', 'Author'],
    ['genre', 'Genre'],
    ['rating', 'Rating'],
    ['summary', 'Summary']
  ];

  return (
    <section className="table-shell" aria-label="Book table">
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              {headers.map(([key, label]) => (
                <th key={key}>
                  <button type="button" onClick={() => setColumn(key)}>
                    {label}
                    <span>{sort.key === key ? (sort.direction === 'asc' ? '↑' : '↓') : ''}</span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((book) => (
              <tr key={book.id}>
                <td>{book.title}</td>
                <td>{book.author}</td>
                <td><span className="genre-pill">{book.genre || 'Book'}</span></td>
                <td><StarRating rating={book.rating} /></td>
                <td>{book.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Details({ item, mode }) {
  if (!item || mode === 'gallery') return <section className="details empty" aria-hidden="true" />;

  return (
    <section className="details">
      <h2>{item.title}</h2>
      <p className="byline">{item.author}</p>
      <div className="meta-row">
        <StarRating rating={item.rating} />
        <span>{item.genre || 'Book'}</span>
      </div>
      <p className="summary">{item.summary || 'No summary available.'}</p>
    </section>
  );
}

function App() {
  const [mode, setMode] = useState('library');
  const [books, setBooks] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [state, setState] = useState({ loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch('/data/books.json').then((res) => {
        if (!res.ok) throw new Error('Could not load books.');
        return res.json();
      }),
      fetch('/data/photos.json').then((res) => {
        if (!res.ok) throw new Error('Could not load photos.');
        return res.json();
      })
    ])
      .then(([bookData, photoData]) => {
        if (cancelled) return;
        setBooks(bookData);
        setPhotos(photoData);
        setState({ loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredBooks = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return books;
    return books.filter((book) => [book.title, book.author, book.genre, book.summary].join(' ').toLowerCase().includes(needle));
  }, [books, query]);

  const activeItems = mode === 'gallery' ? photos : filteredBooks;
  const activeItem = activeItems[Math.min(activeIndex, Math.max(0, activeItems.length - 1))];

  useEffect(() => {
    setActiveIndex(0);
  }, [mode, query]);

  if (state.loading) {
    return (
      <div className="app-shell app-center">
        <div className="loading-card">
          <BookOpen size={36} />
          <p>Loading archive...</p>
        </div>
      </div>
    );
  }

  if (state.error) return <ErrorBoundaryFallback error={state.error} />;

  return (
    <div className="app-shell">
      <header className="site-header">
        <div className="title-block">
          <h1>Spencer Reads</h1>
          <p>{mode === 'gallery' ? `${photos.length} photos` : `${filteredBooks.length} books`} · 2021-2025</p>
        </div>

        <div className="controls">
          <label className="search-box">
            <Search size={16} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search books"
              aria-label="Search books"
              disabled={mode === 'gallery'}
            />
          </label>

          <div className="segmented" aria-label="View mode">
            <button type="button" className={mode === 'library' ? 'active' : ''} onClick={() => setMode('library')}>
              <BookOpen size={17} /> Library
            </button>
            <button type="button" className={mode === 'table' ? 'active' : ''} onClick={() => setMode('table')}>
              <Table2 size={17} /> Table
            </button>
            <button type="button" className={mode === 'gallery' ? 'active' : ''} onClick={() => setMode('gallery')}>
              <Camera size={17} /> Photos
            </button>
          </div>
        </div>
      </header>

      {mode === 'table' ? (
        <BookTable books={filteredBooks} />
      ) : (
        <>
          <CoverFlow items={activeItems} mode={mode} activeIndex={activeIndex} setActiveIndex={setActiveIndex} />
          <Details item={activeItem} mode={mode} />
        </>
      )}
    </div>
  );
}

createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
